/**
 * Transfer-ownership modal. Owner-only. Posts to /api/cars/{id}/transfer
 * with new_owner_login + current_password. Backend creates a notification
 * for the new owner; we just confirm submission was accepted.
 */

import { useEffect, useState, type FormEvent } from "react";

import Modal from "../../components/Modal";
import { ApiError } from "../../api/client";
import { useTransferCar } from "../../api/cars";

interface TransferCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  carId: number;
  carName: string;
}

export default function TransferCarModal({
  isOpen,
  onClose,
  carId,
  carName,
}: TransferCarModalProps) {
  const transfer = useTransferCar(carId);
  const [newOwner, setNewOwner] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submittedOk, setSubmittedOk] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewOwner("");
      setPassword("");
      setError(null);
      setSubmittedOk(false);
    }
  }, [isOpen]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await transfer.mutateAsync({
        new_owner_login: newOwner.trim(),
        current_password: password,
      });
      setSubmittedOk(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Wrong password.");
      } else if (err instanceof ApiError) {
        setError(err.detail ?? err.message);
      } else {
        setError("Could not start transfer.");
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transfer ownership">
      {submittedOk ? (
        <div className="auth-form">
          <p>
            Transfer request sent. <strong>{carName}</strong> will move to its
            new owner once they accept the notification.
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <p>
            Transfer <strong>{carName}</strong> to another user. They'll get a
            notification and must accept before the change takes effect.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="transfer-new-owner">
              New owner (username or email)
            </label>
            <input
              id="transfer-new-owner"
              className="field__input"
              type="text"
              required
              autoCapitalize="none"
              autoCorrect="off"
              value={newOwner}
              onChange={(e) => setNewOwner(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="transfer-pw">
              Your password
            </label>
            <input
              id="transfer-pw"
              className="field__input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <p className="form-status" data-variant="error" role="alert">
              {error}
            </p>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="button"
              onClick={onClose}
              disabled={transfer.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--danger"
              disabled={transfer.isPending || !newOwner.trim() || !password}
            >
              {transfer.isPending ? "Sending…" : "Send transfer request"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
